import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Col, Row, Table, OverlayTrigger, Tooltip, Tab, Badge } from 'react-bootstrap';
import moment from 'moment/min/moment-with-locales';

//components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '../../../hoc/withLocalization';
import InputTime from '../../../components/GenericForm/Widgets/InputTime';
import SwitchWidget from '../ControlPanel/SwitchWidget';
import { CostUnitsAndTypeWidget } from '../../../components/GenericForm/Widgets/CostUnitsAndTypes';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import PayrollHistoryTable from '../../../components/GenericModal/PayrollHistoryTable.js';
import UserPromptModal from '../../../components/GenericModal/userPromptModal/index.js';

//elements
import { Textarea } from '../../../elements/Input';
import LoadingSpinner from '~/elements/LoadingSpinner';
import { NumberInput } from '../../../elements/Input';
import Select from '../../../elements/Select.js';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../utils/timeCalc';
import { timeFormat, shortDateFormat, dateToTimeFormat } from '~/utils/dateFormat';
import { ratioToPercent, resolvePayrollWageTypeLabel, mergeExtraOvertimeTypes } from '~/utils/commonMethods';
import { totalTimeFormat } from '../../../utils/dateFormat.js';

//assets
import ResetApprovalIcon from '../../../assets/img/resetApprovalIcon.svg';
import ApprovalIcon from '../../../assets/img/ApprovalIcon.svg';
import RejectIcon from '../../../assets/img/RejectIcon.svg';

class MultiTaskModalAccept extends Component {
    constructor(props) {
        super(props);
        this.currentIntegrationProduct = props?.commonStore?.config?.integration_details?.product || null;
        this.state = {
            currentStatus: 'active',
            isLoading: true,
            syncWithIntegration: false,
            shiftDetails: [],
            validationMessage: '',
            overtimes: {},
            weekly_overtimes: {},
            overtimeArray: [],
            cost_units: null,
            costUnitsHasError: false,
            currentTab: 'shifts',
            shiftAllowance: {},
            isAlreadySynced: false,
            timelog: {},
            timelogTasksOptions: [],
            payrollDetails: [],
            admin_note: "",
            notes: '',
            userPromptModal: {
                source: '',
                header: '',
                showModal: false,
                sourceInfo: {},
                actions: [],
                actionMethods: {}
            }
        };
        this.prepareTasksOption = this.prepareTasksOption.bind(this);
    }

    componentDidMount() {
        this.loadData();
    }

    getShiftDetails = async (start, end, userId, isModalView, isLoadShiftAllowance) => {
        const dateTimeRules = this.getDateTimeRules();
        const { commonStore, scheduleStore, t } = this.props;
        try {
            const paramsObj = {
                start: start,
                end: end,
                userID: userId,
                isModalView: isModalView,
                isLoadShiftAllowance: isLoadShiftAllowance
            }
            const shiftDetails = await scheduleStore.loadShiftsForApproval(paramsObj, dateTimeRules);
            if (isLoadShiftAllowance) {
                this.setState({ shiftAllowance: shiftDetails.shiftAllowance });
            }
            return shiftDetails.shifts;
        } catch (err) {
            console.error({ err })
            commonStore.addNotification(t(typeof err === 'string' ? err : 'Something went wrong'), null, 'error');
        }
    }

    prepareTasksOption(array) {
        const dateTimeRules = this.getDateTimeRules();
        const options = array.map((ele) => {
            const fromTime = timeFormat(ele.from, dateTimeRules);
            const toTime = timeFormat(ele.to, dateTimeRules);
            const timeRange = ` (${fromTime} - ${toTime})`;

            const maxNameLength = 25;
            let displayName = ele.task_name.length > maxNameLength
                ? ele.task_name.slice(0, maxNameLength - 3) + '...'
                : ele.task_name;

            return {
                value: `${ele.id}`,
                label: `${displayName}${timeRange}`,
                title: `${ele.task_name} (${fromTime} - ${toTime})`,
                ...ele
            };
        });
        return options;
    }

    async loadData() {
        const { timelogStore, commonStore, t, selectedUserId, selectedForDate } = this.props;
        const clientInfo = commonStore.config;
        const isIntegrationAvailable = !!this.currentIntegrationProduct;
        const timelogs = await timelogStore.forDateLoadList(selectedUserId, selectedForDate) || [];
        const timelog = timelogs.rows[timelogs.rows.length - 1];

        let _payrollDetails
        if (timelog.status === 'approved' && isIntegrationAvailable)
            _payrollDetails = await timelogStore.forDateLoadIntList(selectedUserId, selectedForDate) || [];

        const timelogTasksOptions = this.prepareTasksOption(timelogs.rows);
        const shiftDetails = await this.getShiftDetails(timelog.for_date, timelog.for_date, timelog.user_id, true, true);
        timelog['model_name'] = 'timelog';
        let validateTimelogWithIntegration = '';
        if (isIntegrationAvailable) {
            validateTimelogWithIntegration = await timelogStore.validateTimelogWithIntegration(timelog);
        }
        else {
            validateTimelogWithIntegration = 'No integration available';
        }
        const isAlreadySynced = isIntegrationAvailable && ((timelog?.int_id && timelog.int_status === 'success') || timelog.int_status === 'failed') ? true : false
        let overtimes = timelog.data.overtimes;
        let overtimeArray = clientInfo.client.data.basicRules.overtime_types;

        if (!clientInfo.client.allow_company_shifts && clientInfo.client.data.basicRules.overtimeAutomaticMode) {
            const code = commonStore.config.client.data.basicRules.overtimeCalcRules.calc_percent;
            if (timelog._total_hours_overtime) {
                overtimes = {
                    [code]: {
                        code: `${clientInfo.client.data.basicRules.overtimeCalcRules.calc_percent}`,
                        value: minutesToTimeInput(timelog._total_hours_overtime, false),
                        name: `Automatic Overtime ${clientInfo.client.data.basicRules.overtimeCalcRules.calc_percent}%`
                    }
                }
                overtimeArray = [overtimes[code]]
            }
        }
        else if (clientInfo.client.allow_company_shifts) {
            overtimeArray = [];
            if (commonStore?.config?.overtimes?.length) {
                commonStore.config.overtimes.forEach(ot => {
                    overtimeArray.push({
                        code: ot.overtime,
                        multiplier: ot.overtime,
                        name: ot.name
                    })
                })
            }
            // Include overtime types already recorded on this timelog (e.g. urgent work, weekly overtime)
            // even if they aren't part of the assigned company shift's configured overtime list.
            overtimeArray = mergeExtraOvertimeTypes(
                overtimeArray,
                [timelog.data?.overtimes, timelog.data?.weekly_overtimes],
                commonStore?.config?.overtimes
            );
        }

        this.setState({
            currentStatus: timelog.status,
            notes: timelog.status_note || '',
            admin_note: timelog.admin_note || '',
            validationMessage: t(validateTimelogWithIntegration),
            shiftDetails: shiftDetails,
            syncWithIntegration: isAlreadySynced || !validateTimelogWithIntegration,
            isAlreadySynced: isAlreadySynced,
            overtimes: overtimes,
            weekly_overtimes: timelog?.data?.weekly_overtimes || {},
            overtimeArray: overtimeArray,
            cost_units: timelog?.data?.cost_units || null,
            currentTab: this.props.initialTab || this.getFirstNonEmptyTab(shiftDetails, overtimes, timelog),
            timelog: timelog,
            timelogTasksOptions: timelogTasksOptions,
            payrollDetails: _payrollDetails || []
        }, () => this.setState({ isLoading: false }));
    }

    getFirstNonEmptyTab = (shiftDetails, overtimes, timelog) => {
        if (shiftDetails?.length > 0) return 'shifts';
        if (timelog?.data?.timeBank?.length > 0) return 'timebank';
        if ((timelog?.data?.overtimes && Object.values(timelog.data.overtimes).length > 0) || (timelog?.data?.weekly_overtimes && Object.values(timelog.data.weekly_overtimes).length > 0)) return 'overtime';
        if (timelog?.data?.extraPayments && Object.values(timelog.data.extraPayments).length > 0) return 'addtionals';
        return 'shifts';
    };

    async handleRun(action) {
        if (action === 'rejected' && !this.state.notes) {
            this.props.commonStore.addNotification(this.props.t('Comment is required'), null, 'error');
            return;
        }
        this.saveAndSetStatus(action);
    }

    saveAndSetStatus = async (action) => {
        const { timelogStore, commonStore, t } = this.props;
        const { timelog, notes, admin_note, syncWithIntegration } = this.state;
        this.setState({ isLoading: true })
        try {
            const body = {
                user_id: timelog.user_id,
                for_date: timelog.for_date,
                syncWithIntegration: syncWithIntegration,
                status: action,
                notes: notes,
                admin_note: admin_note
            }
            await timelogStore.multiTimelogStatus(body);
            if (syncWithIntegration) commonStore.addNotification(t(`Syncing complete! The timelogs is now synchronized with`) + " " + this.currentIntegrationProduct, null, 'success');
            else commonStore.addNotification(t('Saved'), null, 'success');
            this.props.closeModal();
        } catch (err) {
            console.error(err);
            const approvedWeeklyOvertime = err?.response?.body?.userPrompts?.find(ele => ele.source === 'approvedWeeklyOvertime');
            if (approvedWeeklyOvertime?.showModal) {
                this.setState({
                    userPromptModal: {
                        ...approvedWeeklyOvertime,
                        actionMethods: {
                            handleCloseUserPromptModal: this.handleCloseUserPromptModal,
                            handleResetApprovedWeeklyOvertime: () => this.handleResetApprovedWeeklyOvertime(approvedWeeklyOvertime.sourceInfo.approvedWeeklyOvertime.approvedTimesheets)
                        }
                    }
                });
            }
        }
        finally {
            this.setState({ isLoading: false })
        }
    }

    handleCloseUserPromptModal = () => {
        this.setState({ userPromptModal: { ...this.state.userPromptModal, showModal: false } })
    }

    handleResetApprovedWeeklyOvertime = async (approvedTimesheets) => {
        const { timelogStore, commonStore, t } = this.props;
        this.setState({ isLoading: true })
        try {
            await timelogStore.resetApprovedWeeklyOvertimeTimesheets(approvedTimesheets);
            this.handleCloseUserPromptModal();
            commonStore.addNotification(t('Approved timesheets have been reset successfully.'), null, 'success');
            this.props.closeModal();
        } catch (err) {
            console.error(err);
        }
        finally {
            this.setState({ isLoading: false })
        }
    }

    isStatusUpdated = () => {
        const { currentStatus } = this.state
        return currentStatus !== 'active';
    }

    isResetButtonDisabled = () => {
        const { timelogTasksOptions } = this.state;
        return timelogTasksOptions.length && timelogTasksOptions.some(task => ['approved', 'rejected'].includes(task.status));
    }

    handleSyncSwitch = (value) => {
        this.setState({ syncWithIntegration: value })
    }

    getDateTimeRules = () => {
        const { commonStore } = this.props;
        return commonStore.config.client && commonStore.config.client.data &&
            commonStore.config.client.data.dateTimeRules
            ? commonStore.config.client.data.dateTimeRules
            : false;
    }

    handleCostUnitChange = (value) => {
        this.setState({ cost_units: value });
    }

    handleCostUnitsError = (value, hasError) => {
        const { t, commonStore } = this.props;
        this.setState({ cost_units: value, costUnitsHasError: hasError });
        const errorMsg = t("Some of the previously selected cost-units had expired, system removed them to avoid inconsistency, if needed update on payroll and then refetch here.");
        commonStore.addNotification(errorMsg, null, 'warning', "top-right", 3000);
    }

    handletaskChange(task) {
        this.setState({ timelog: task }, () => this.setState({
            overtimes: this.state.timelog?.data?.overtimes || {},
            weekly_overtimes: this.state.timelog?.data?.weekly_overtimes || {},
            cost_units: this.state.timelog?.data?.cost_units
        }))
    }

    render() {
        if (this.state.isLoading)
            return <LoadingSpinner />;

        const { t, commonStore, userStore } = this.props;
        const { currentTab, shiftAllowance, overtimes, overtimeArray, timelog, timelogTasksOptions, payrollDetails, weekly_overtimes, userPromptModal } = this.state;
        const overtimesCount = (overtimes ? Object.keys(overtimes).length : 0) + (weekly_overtimes ? Object.keys(weekly_overtimes).length : 0);
        const allowTimeBank = commonStore?.config?.client?.data?.basicRules?.timeBankSettings?.allowTimeBank;
        const dateTimeRules = this.getDateTimeRules();

        const { user_type, id: currentUserId } = userStore.currentUser;
        const preventPmSelfApproval = commonStore?.config?.client?.data?.basicRules?.preventPmSelfApproval;
        const isSelfApprovalBlocked = preventPmSelfApproval && user_type === 'pm' && parseInt(timelog.user_id) === parseInt(currentUserId);
        const selfApprovalMessage = t('You cannot approve, reject or reset your own request');
        const payrollDetailsForTimelog = payrollDetails?.rows?.length ? payrollDetails.rows.find(ele => ele.id === timelog.id)?.int_data_array : [];

        return (
            <div className="mb-5">
                <div className="row timelog-grid-time-card mt-2">
                    <Col xs={12} md={6}
                        className="timelog-grid-item-1 justify-content-space-between card-timelog-grid-item mb-3"
                    >
                        <div className="d-flex">
                            <i className="timelog-grid__icon"></i>
                            <span className="timelog-grid__text" style={{ width: 'fit-content', textTransform: 'capitalize' }}>
                                {timelog.first_name} {timelog.last_name}
                            </span>
                        </div>
                        <b className="timelog-final-time-card-modal align-self-center">
                            {t('Remuneration type')} - {t(timelog.payment_mode)}
                        </b>
                    </Col>
                    <Col xs={12} md={6}
                        className="timelog-grid-item-2 justify-content-space-between card-timelog-grid-item mb-3"
                    >
                        <div className="d-flex align-items-center">
                            <i className="timelog-grid__icon"></i>
                            <span className="timelog-grid__text">{t('For Date')}</span>
                        </div>
                        <b className="timelog-final-time-card-modal align-self-center">
                            {timelog.status === 'active' && timelog.from > timelog.to ?
                                `${shortDateFormat(timelog.for_date, dateTimeRules)} | ${shortDateFormat(moment(timelog.for_date).add(1, 'days'), dateTimeRules)}` :
                                shortDateFormat(timelog.for_date, dateTimeRules)
                            }
                        </b>
                    </Col>
                    <Col xs={12} md={6} className="d-flex pt-1 mb-2">
                        <div className="fw-600 color-2550AC me-2 pt-2">{t('Tasks')}:</div>
                        <Select
                            options={timelogTasksOptions}
                            onChange={(task) => this.handletaskChange(task)}
                            value={timelogTasksOptions.find(opt => opt.value === `${timelog.id}`)}
                            style={{ width: "350px" }}
                        />
                    </Col>
                    <Col xs={12} md={6}
                        className="timelog-grid-item justify-content-space-between card-timelog-grid-item mb-2"
                    >
                        <div className="d-flex align-items-center">
                            <i className="timelog-grid__icon"></i>
                            <span className="timelog-grid__text w-auto">{t('Total Hours')}</span>
                        </div>
                        <b className="timelog-final-time-card-modal align-self-center">
                            <b className="timelog-final-time-card-modal align-self-center">
                                {minutesToTimeInput(this.state.timelogTasksOptions?.reduce((sum, timelog) => sum + (timelog.total_hours_normal || 0), 0))}
                            </b>
                        </b>
                    </Col>
                </div>
                {timelog.auto_logout && timelog.status === 'active' &&
                    <span className="d-flex font-size-12 p-2 text-danger align-items-center fw-600">
                        <span className='font-size-15'><strong>{t("Note")}:&nbsp;</strong></span>
                        {t("This log was auto clocked out for exceeding the configured work hour limit. Please review and save it to enable related calculations like overtime and additional payments.")}
                    </span>
                }
                {(timelog.anomaly?.start?.is_early || timelog.anomaly?.start?.is_late || timelog.anomaly?.start?.is_offsite || timelog.anomaly?.end?.is_early || timelog.anomaly?.end?.is_late || timelog.anomaly?.end?.is_offsite) ?
                    <div className='row gx-0 justify-content-center mt-2 fw-bold'>
                        {timelog.anomaly?.start?.is_early || timelog.anomaly?.start?.is_late || timelog.anomaly?.start?.is_offsite ?
                            <>
                                <Row className='w-100'>
                                    <Col className='d-flex justify-content-around' xs={12} md={6}>
                                        <div>{t('Clock In')}:&nbsp;</div>
                                        <div className={`d-flex align-items-center ${timelog.anomaly?.start?.is_late ? 'text-danger' : timelog.anomaly?.start?.is_early ? "text-warning " : "text-success"}`}>
                                            <i className='fa fa-clock-o' />
                                            {timelog.anomaly?.start?.is_late ? t('Late') : timelog.anomaly?.start?.is_early ? t('Early') : t('On-time')}
                                        </div>
                                    </Col>
                                    <Col className='d-flex justify-content-around' xs={12} md={6}>
                                        <div>{t('Location')}:&nbsp;</div>
                                        <div className={`d-flex align-items-center ${timelog.anomaly?.start?.is_offsite ? "text-danger d-flex align-items-center" : "text-success"}`}>
                                            <i className='fa fa-map-marker' />
                                            {timelog.anomaly?.start?.is_offsite ? t('Off-site') : t('On-site')}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className='w-100 mt-2 d-flex mb-3'>
                                    <div className='d-flex justify-content-start border-bottom'>
                                        <div className='w-25 d-md-flex justify-content-md-center mb-2'>{`${t('Comment')}`}:&nbsp;</div>
                                        <div className='w-75 text-info'>{t(timelog.anomaly?.start?.comment)}</div>
                                    </div>
                                </Row>
                            </>
                            : null
                        }

                        {timelog.anomaly?.end?.is_early || timelog.anomaly?.end?.is_late || timelog.anomaly?.end?.is_offsite ?
                            <>
                                <Row className='w-100'>
                                    <Col className='d-flex justify-content-around' xs={12} md={6}>
                                        <div>{t('Clock Out')}:&nbsp;</div>
                                        <div className={timelog.anomaly?.end?.is_late ? "text-danger" : timelog.anomaly?.end?.is_early ? "text-warning" : "text-success"}>
                                            <i className='fa fa-clock-o' />
                                            {timelog.anomaly?.end?.is_late ? t('Late') : timelog.anomaly?.end?.is_early ? t('Early') : t('On-time')}
                                        </div>
                                    </Col>
                                    <Col className='d-flex justify-content-around' xs={12} md={6}>
                                        <div>{t('Location')}:&nbsp;</div>
                                        <div className={timelog.anomaly?.end?.is_offsite ? "text-danger" : "text-success"}>
                                            <i className='fa fa-map-marker' />
                                            {timelog.anomaly?.end?.is_offsite ? t('Off-site') : t('On-site')}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className='w-100 mt-2 mb-2'>
                                    <Col xs={12} md={12}>
                                        <div className='d-flex justify-content-start'>
                                            <div className='w-25 d-md-flex justify-content-md-center'>{`${t('Comment')}`}:&nbsp;</div>
                                            <div className='w-75 text-info'>{t(timelog.anomaly?.end?.comment)}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                            : null
                        }
                    </div>
                    : null
                }

                <Row>
                    <Col>
                        <Row>
                            <div className="row timelog-grid-time-card mt-2">
                                <Col xs={12} md={6}
                                    className="timelog-grid-item-1 justify-content-space-between card-timelog-grid-item mb-3"
                                >
                                    <div className="d-flex">
                                        <i className="timelog-grid__icon"></i>
                                        <span className="timelog-grid__text">{t('Start')}</span>
                                    </div>
                                    <b className="timelog-final-time-card-modal">
                                        {timeFormat(timelog.from, dateTimeRules)}
                                    </b>
                                </Col>
                                <Col xs={12} md={6}
                                    className="timelog-grid-item-2 justify-content-space-between card-timelog-grid-item mb-3"
                                >
                                    <div className="d-flex align-items-center">
                                        <i className="timelog-grid__icon"></i>
                                        <span className="timelog-grid__text">{t('End')}</span>
                                    </div>
                                    <b className="timelog-final-time-card-modal">
                                        {timeFormat(timelog.to, dateTimeRules)}
                                    </b>
                                </Col>
                            </div>
                            <div className="row timelog-grid-time-card">
                                <Col xs={12} md={6}
                                    className="timelog-grid-item-1 justify-content-space-between card-timelog-grid-item mb-2"
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="timelog-grid__text ml-30">{t('Breaktime')}</span>
                                    </div>
                                    <b className="timelog-final-time-card-modal">
                                        {minutesToTimeInput(timelog.break || 0)}
                                    </b>
                                </Col>
                                <Col xs={12} md={6}
                                    className="timelog-grid-item-2 justify-content-space-between card-timelog-grid-item mb-2"
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="timelog-grid__text ml-30">{t('Total')}</span>
                                    </div>
                                    <b className="timelog-final-time-card-modal">
                                        {getMinutesFromTimeInput(timelog.from) > getMinutesFromTimeInput(timelog.to) ?
                                            minutesToTimeInput(1440 - (timelog.from) + (timelog.to) - (timelog.break || 0)) :
                                            minutesToTimeInput((timelog.to) - (timelog.from) - (timelog.break || 0))
                                        }
                                    </b>
                                </Col>
                            </div>
                        </Row>
                        {timelog.status === 'rejected' &&
                            <div className='text-danger mb-2'>
                                {`${t('Note')}: ${t('Overtimes and Timebank hours are removed as this timesheet was rejected, on reset it will be added automatically if applicable')}`}
                            </div>
                        }
                        <Row>
                            <Col sm={4} className='mt-2'>
                                <ul className="list-group">
                                    {timelog.status === 'approved' &&
                                        <li className={currentTab === 'payroll' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                            onClick={() => this.setState({ currentTab: 'payroll' })}>
                                            <div>
                                                {t('Payroll')}
                                                <Badge className='ms-2 bg-secondary float-end'>{payrollDetailsForTimelog?.length}</Badge>
                                            </div>
                                        </li>
                                    }
                                    <li className={currentTab === 'shifts' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                        onClick={() => this.setState({ currentTab: 'shifts' })}>
                                        <div>
                                            {t('Shifts')}
                                            <Badge className='ms-2 bg-secondary float-end'>{this.state?.shiftDetails?.length}</Badge>
                                        </div>
                                    </li>
                                    {allowTimeBank &&
                                        <li className={currentTab === 'timebank' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                            onClick={() => this.setState({ currentTab: 'timebank' })}>
                                            <div>
                                                {t('Time Bank')}
                                                <Badge className='ms-2 bg-secondary float-end'>{timelog?.data?.timeBank?.length || 0}</Badge>
                                            </div>
                                        </li>
                                    }
                                    <li className={currentTab === 'overtime' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                        onClick={() => this.setState({ currentTab: 'overtime' })}>
                                        <div>
                                            {t('Overtime Hours')}
                                            {timelog.isOvertimeCommentMissing &&
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id="tooltip-top">
                                                            {t('Comment for overtime is missing')}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i className="fa fa-exclamation-triangle text-warning ms-2" aria-hidden="true"></i>
                                                </OverlayTrigger>
                                            }
                                            <Badge className='ms-2 bg-secondary float-end'>{overtimesCount}</Badge>
                                        </div>
                                    </li>
                                    <li className={currentTab === 'addtionals' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                        onClick={() => this.setState({ currentTab: 'addtionals' })}>
                                        <div>
                                            {t('Additional Hours')}
                                            <Badge className='ms-2 bg-secondary float-end'>{timelog.data?.extraPayments ? Object.values(timelog.data?.extraPayments).length : 0}</Badge>
                                        </div>
                                    </li>
                                    {this.state.cost_units &&
                                        <li className={currentTab === 'costUnits' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                            onClick={() => this.setState({ currentTab: 'costUnits' })}>
                                            <div>
                                                {t('Cost Units')}
                                                {this.state.costUnitsHasError &&
                                                    <OverlayTrigger
                                                        placement="right"
                                                        overlay={
                                                            <Tooltip id="tooltip-top">
                                                                {t('Please make sure to save and accept as some of the expired cost units had been removed')}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="fa fa-exclamation-circle text-danger m-2" aria-hidden="true"></i>
                                                    </OverlayTrigger>
                                                }
                                                <Badge className='ms-2 bg-secondary float-end'>{JSON.parse(this.state.cost_units)?.length}</Badge>
                                            </div>
                                        </li>
                                    }
                                    {!!this.currentIntegrationProduct &&
                                        <li className={currentTab === 'payrollHistory' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                            onClick={() => this.setState({ currentTab: 'payrollHistory' })}>
                                            <div>
                                                {t('Payroll')} {t('History')}
                                                <Badge className='ms-2 bg-secondary float-end'>{timelog.data?.integrationSyncHistory ? timelog.data?.integrationSyncHistory.length : 0}</Badge>
                                            </div>
                                        </li>
                                    }
                                </ul>
                            </Col>
                            <Col sm={8}>
                                <Tab.Container activeKey={currentTab} id='modal-accept'>
                                    <Tab.Content>
                                        {payrollDetailsForTimelog.length ?
                                            <Tab.Pane eventKey="payroll">
                                                <h5 className='fw-600'>{t('Payroll details')}</h5>
                                                <div className='timelog-array__right w-100'>
                                                    <Table className='w-100 '>
                                                        <thead>
                                                            <tr className='w-100'>
                                                                <th width={'25%'}>{t('Name')}</th>
                                                                <th width={'25%'}>{t('Hours')}</th>
                                                                <th width={'25%'}>{t('Status')}</th>
                                                                <th width={'25%'}>{t('Wage Type')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {payrollDetailsForTimelog.map((ele, idx) => (
                                                                <tr key={idx} className='text-capitalize mt-1'>
                                                                    <td width={'25%'} className=''><div className='d-flex align-items-center justify-content-center min-height-20 ps-2'>{ele.name}</div></td>
                                                                    <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(ele.hours * 60, dateTimeRules)}</td>
                                                                    <td width={'25%'} className='timelog_array_details'>{ele.status}</td>
                                                                    <td width={'25%'} className='timelog_array_details'>{resolvePayrollWageTypeLabel(ele.payrollWageType, commonStore)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Tab.Pane>
                                            : null
                                        }
                                        <Tab.Pane eventKey="shifts">
                                            <h5 className='fw-600'>{t('Shifts')}</h5>
                                            {this.state.shiftDetails?.length ?
                                                <div className='timelog-array__right w-100'>
                                                    <Table className='w-100 '>
                                                        <thead>
                                                            <tr className='w-100'>
                                                                <th width={'25%'}>{t('Project')}</th>
                                                                <th width={'25%'}>{t('Task')}</th>
                                                                <th width={'25%'}>{t('Date')}</th>
                                                                <th width={'25%'}>{t('Time')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {this.state.shiftDetails.map((ele, idx) => (
                                                                <tr key={idx} className='text-capitalize mt-1'>
                                                                    <td width={'25%'} className=''><div className='d-flex align-items-center justify-content-center min-height-20 ps-2'>{ele.projectName}</div></td>
                                                                    <td width={'25%'} className='timelog_array_details'>{ele.taskName}</td>
                                                                    <td width={'25%'} className='timelog_array_details'>{shortDateFormat(ele.forDate, dateTimeRules)}</td>
                                                                    <td width={'25%'} className='timelog_array_details'>{ele.title}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                                :
                                                <div className='text-center'>
                                                    {t('No Shifts Found for the day')}!
                                                </div>
                                            }
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="timebank">
                                            <h5 className='fw-600 mb-1'>{t('Time Bank')}</h5>
                                            {timelog.data?.timeBank && timelog.data?.timeBank.length ?
                                                <>
                                                    <Row>
                                                        <Table className='w-100 mt-0'>
                                                            <thead>
                                                                <tr className='w-100'>
                                                                    <th width={'25%'}>{t('Name')}</th>
                                                                    <th width={'25%'}>{t('Hours')}</th>
                                                                    <th width={'25%'}>{t('Ratio')}</th>
                                                                    <th width={'25%'}>{t('Calculated Hours')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {timelog.data?.timeBank.map((ele, idx) => (
                                                                    ele.value ?
                                                                        <tr key={idx} className='text-capitalize mt-1'>
                                                                            <td width={'25%'} className=''><div className='d-flex align-items-center justify-content-center min-height-20 ps-2'>{t('Time Bank')} {t('Ratio')} {ele.type === 'normal' ? `(${t('flexitime')})` : `(${ele.type} ${ratioToPercent(ele?.ratio)}%)`}</div></td>
                                                                            <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(ele.value, dateTimeRules)}</td>
                                                                            <td width={'25%'} className='timelog_array_details'>{ele.ratio}</td>
                                                                            <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(ele.value * ele.ratio, dateTimeRules)}</td>
                                                                        </tr>
                                                                        : null
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Row>
                                                </>
                                                :
                                                <div className='text-center'>
                                                    {t('No Timebank Record Found')}!
                                                </div>}
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="overtime">
                                            <div className='fw-600'>
                                                {t('Overtimes Hours')}
                                                {timelog.isOvertimeCommentMissing &&
                                                    <span className='text-warning ms-2'>
                                                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
                                                        {t('Comment for overtime is missing')}
                                                    </span>
                                                }
                                            </div>
                                            {overtimesCount ?
                                                <>
                                                    <div className="row timelog-grid-time-card">
                                                        <Table className='w-100 mt-0'>
                                                            <thead>
                                                                <tr className='w-100'>
                                                                    <th width={'25%'}>{t('Name')}</th>
                                                                    <th width={'25%'}>{t('Hours')}</th>
                                                                    <th width={'25%'}>{t('Ratio')}</th>
                                                                    <th width={'25%'}>{t('Calculated Hours')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {overtimeArray && overtimeArray.length && !!overtimes &&
                                                                    overtimeArray.map((overtime, index) => (
                                                                        overtimes[overtime?.code]?.value ?
                                                                            <tr key={index} className='text-capitalize mt-1'>
                                                                                <td width={'25%'} className=''><div className='d-flex align-items-center justify-content-center min-height-20 ps-2'>{overtime.name}</div></td>
                                                                                <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(overtimes[overtime.code] ? getMinutesFromTimeInput(overtimes[overtime.code].value) : 0, dateTimeRules)}</td>
                                                                                <td width={'25%'} className='timelog_array_details'>{parseInt(overtime.code) / 100 + 1}</td>
                                                                                <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(overtimes[overtime.code] ? getMinutesFromTimeInput(overtimes[overtime.code].value) * (parseInt(overtime.code) / 100 + 1) : 0, dateTimeRules)}</td>
                                                                            </tr>
                                                                            : null
                                                                    ))}
                                                                {overtimeArray && overtimeArray.length && !!weekly_overtimes &&
                                                                    overtimeArray.map((overtime, index) => (
                                                                        weekly_overtimes[overtime?.code]?.value ?
                                                                            <tr key={index} className='text-capitalize mt-1'>
                                                                                <td width={'25%'} className=''><div className='d-flex align-items-center justify-content-center min-height-20 ps-2'>{weekly_overtimes[overtime.code].name}</div></td>
                                                                                <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(weekly_overtimes[overtime.code] ? getMinutesFromTimeInput(weekly_overtimes[overtime.code].value) : 0, dateTimeRules)}</td>
                                                                                <td width={'25%'} className='timelog_array_details'>{parseInt(overtime.code) / 100 + 1}</td>
                                                                                <td width={'25%'} className='timelog_array_details'>{totalTimeFormat(weekly_overtimes[overtime.code] ? getMinutesFromTimeInput(weekly_overtimes[overtime.code].value) * (parseInt(overtime.code) / 100 + 1) : 0, dateTimeRules)}</td>
                                                                            </tr>
                                                                            : null
                                                                    ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </>
                                                :
                                                <div className='text-center'>
                                                    {t('No Overtime Record Found for the day')}!
                                                </div>
                                            }
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="addtionals">
                                            <h5 className='fw-600'>{t('Additional Hours')}</h5>
                                            {timelog.data?.extraPayments && Object.values(timelog.data?.extraPayments).length ? (
                                                <>
                                                    {commonStore.config?.client?.data?.extraPayments?.length &&
                                                        <div className='font-size-15 fw-semibold' style={{ color: '#446bbb' }}>
                                                            {t('Time&Control suggests that you double-check all the additional payments before approving and sending them to payroll to avoid any mistakes')}.
                                                        </div>
                                                    }
                                                    <div className="row timelog-grid-time-card">
                                                        {commonStore.config.client.data?.extraPayments.length &&
                                                            commonStore.config.client.data.extraPayments.map((extraPayment, index) =>
                                                                timelog.data?.extraPayments[extraPayment.code]?.value ? (
                                                                    <Col xs={12} sm={12} md={6} lg={6} xl={6} xxl={6} className='timelog-grid-extra-payment-item_approval justify-content-space-between card-timelog-grid-item ' key={`${index}_${extraPayment.code}`}>
                                                                        <div className='d-flex align-items-center'>
                                                                            <span className="timelog-grid__text timelog-grid__text-capitalize w-100">
                                                                                {extraPayment.name}
                                                                            </span>
                                                                        </div>
                                                                        <OverlayTrigger
                                                                            key={extraPayment?.name}
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-top">{t('Add Additional Payments Hours')}</Tooltip>}
                                                                        >
                                                                            <div>
                                                                                {extraPayment?.extraPaymentType?.value === 'fixed_hourly_amount' || extraPayment.code === 'shift_allowance' ?
                                                                                    <NumberInput
                                                                                        style={{ maxWidth: '85px' }}
                                                                                        customClassses="text-center"
                                                                                        disabled={!extraPayment.isEditable || shiftAllowance?.amount}
                                                                                        value={timelog.data?.extraPayments[extraPayment.code]?.value
                                                                                            ? timelog.data?.extraPayments[extraPayment.code]?.value
                                                                                            : 0}
                                                                                    />
                                                                                    :
                                                                                    <InputTime
                                                                                        value={
                                                                                            timelog.data?.extraPayments[extraPayment.code]?.value
                                                                                                ? minutesToTimeInput(timelog.data?.extraPayments[extraPayment.code]?.value)
                                                                                                : '00:00'
                                                                                        }
                                                                                        className="big-input text-center"
                                                                                    />
                                                                                }
                                                                            </div>
                                                                        </OverlayTrigger>
                                                                    </Col>
                                                                ) :
                                                                    <></>
                                                            )}
                                                        {commonStore.config.client.data?.extendedRules.kilometersRules?.length &&
                                                            commonStore.config.client.data.extendedRules.kilometersRules.map((extraPayment, index) =>
                                                                !!timelog.data?.extraPayments[extraPayment.distance.replaceAll(" ", "").toLocaleLowerCase()]?.value && (
                                                                    <Col xs={12} sm={12} md={6} lg={6} xl={6} xxl={6} className='timelog-grid-extra-payment-item_approval justify-content-space-between card-timelog-grid-item ' key={`${index}_${extraPayment.code}`}>
                                                                        <div className='d-flex align-items-center'>
                                                                            <span className="timelog-grid__text timelog-grid__text-capitalize w-100">
                                                                                {t('Km/Miles Reimbursement')} ({extraPayment.distance})
                                                                            </span>
                                                                        </div>
                                                                        <OverlayTrigger
                                                                            key={extraPayment?.distance}
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-top">{t('These will be automatically filled by the system')}</Tooltip>}
                                                                        >
                                                                            <div>
                                                                                <NumberInput
                                                                                    style={{ maxWidth: '85px' }}
                                                                                    customClassses="text-center"
                                                                                    disabled={!extraPayment.isEditable}
                                                                                    value={timelog.data?.extraPayments[extraPayment.distance.replaceAll(" ", "").toLocaleLowerCase()]?.value
                                                                                        ? timelog.data?.extraPayments[extraPayment.distance.replaceAll(" ", "").toLocaleLowerCase()]?.value
                                                                                        : 0}
                                                                                />
                                                                            </div>
                                                                        </OverlayTrigger>
                                                                    </Col>
                                                                )
                                                            )}
                                                    </div>
                                                </>
                                            ) : <div className='text-center'>
                                                {t('No Additional Hours Registered')}!
                                            </div>
                                            }
                                        </Tab.Pane>
                                        {this.state.cost_units &&
                                            <Tab.Pane eventKey="costUnits">
                                                <h5 className='fw-600'>{t('Cost Units')}</h5>
                                                <CostUnitsAndTypeWidget
                                                    value={timelog?.data?.cost_units}
                                                    onChange={(value) => this.handleCostUnitChange(value)}
                                                    hasErrorCallBack={(value, hasError) => this.handleCostUnitsError(value, hasError)}
                                                />
                                            </Tab.Pane>
                                        }
                                        {!!this.currentIntegrationProduct && <Tab.Pane eventKey="payrollHistory">
                                            <h5 className='fw-600'>{t('Payroll')} {t('history')}</h5>
                                            {timelog.data?.integrationSyncHistory?.length > 0 ? <div className='timelog-array__right w-100'>
                                                <PayrollHistoryTable integrationSyncHistory={timelog.data?.integrationSyncHistory} dateTimeRules={dateTimeRules} />
                                            </div>
                                                :
                                                <div className='text-center'>
                                                    {t('No payroll history found!')}
                                                </div>
                                            }
                                        </Tab.Pane>
                                        }
                                    </Tab.Content>
                                </Tab.Container>
                            </Col>
                        </Row>
                    </Col>
                </Row >
                {timelog.conflictMessage && (
                    <div className="mt-2 mb-1">
                        <span className="fw-bold">{t('Warning')}:&nbsp;</span>
                        <span className="text-warning">{t(timelog.conflictMessage)}</span>
                    </div>
                )}
                <Row className="mt-20">
                    <Col>
                        <Textarea
                            label={`${t('Add')} ${t('Notes')}`}
                            value={this.props.admin_note ? this.props.admin_note : this.state.admin_note}
                            onChange={e => this.setState({ admin_note: e.target.value })}
                            placeholder={t('Enter Notes here')}
                            required
                            timelog_modal={"_timelog"}
                        />
                    </Col>
                    <Col>
                        <Textarea
                            label={t('Comment')}
                            value={this.props.notes ? this.props.notes : this.state.notes}
                            onChange={(e) => this.setState({ notes: e.target.value })}
                            placeholder={t('Enter comments here')}
                            required
                            timelog_modal={"_timelog"}
                        />
                    </Col>
                </Row>
                {timelog.showOvertimeComment &&
                    <Row className="mt-12">
                        <Col>
                            <div className="d-flex align-items-center mb-1">

                                <span className="input-block__label">{t('Overtime Comment')}</span>
                                {timelog.isOvertimeCommentMissing &&
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id="tooltip-top">
                                                {t('Comment for overtime is missing')}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-exclamation-triangle text-warning ms-2" aria-hidden="true"></i>
                                    </OverlayTrigger>
                                }
                            </div>
                            <Textarea
                                value={timelog.overtime_comment || ''}
                                disabled
                                timelog_modal={"_timelog"}
                            />
                        </Col>
                    </Row>
                }
                {!!this.currentIntegrationProduct &&
                    <>
                        <div className='mt-2 d-flex justify-content-start'
                            title={!this.state.validationMessage ? t('Sync with integration') : t(this.state.validationMessage)}>
                            <div className='mt-1'>
                                <SwitchWidget
                                    value={this.state.syncWithIntegration}
                                    onChange={checked => this.handleSyncSwitch(checked)}
                                    disabled={this.isStatusUpdated() || (!this.state.validationMessage && !this.state.isAlreadySynced ? false : true)}
                                />
                            </div>
                            <div>
                                <label className='ml-10'>{t(`Do You want to Save the hours to`)} {this.currentIntegrationProduct}?</label>
                            </div>
                        </div>
                        {this.state.validationMessage &&
                            <span className='text-danger fw-bold font-size-12'>{t('Warning')}:&nbsp;{t(this.state.validationMessage)}</span>
                        }
                    </>
                }
                {
                    <div className='timelog-right-fade-modal-container'>
                        <div className="timelog-modal-accept-buttons">
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-approve">
                                {isSelfApprovalBlocked ? selfApprovalMessage : this.isStatusUpdated() ? t('Please reset first to change approval status') : t('Approve')}
                            </Tooltip>}>
                                <Button fill wd icon style={{ backgroundColor: 'green', border: 0, alignItems: 'center' }}
                                    onClick={() => this.handleRun('approved')}
                                    disabled={this.isStatusUpdated() || isSelfApprovalBlocked}
                                >
                                    <img src={ApprovalIcon} alt="ApprovalIcon" /> {t('Accept original')}
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-reject">
                                {isSelfApprovalBlocked ? selfApprovalMessage : this.isStatusUpdated() ? t('Please reset first to change approval status') : t('Reject')}
                            </Tooltip>}>
                                <Button fill wd icon style={{ backgroundColor: 'red', border: 0 }}
                                    onClick={() => this.handleRun('rejected')}
                                    disabled={this.isStatusUpdated() || isSelfApprovalBlocked}
                                >
                                    <img src={RejectIcon} alt="RejectIcon" /> {t('Reject')}
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-reset">
                                {isSelfApprovalBlocked ? selfApprovalMessage : !this.isResetButtonDisabled() ? t('Pending approval') : t('Reset status')}
                            </Tooltip>}>
                                <Button fill wd icon
                                    style={{ backgroundColor: 'darkorange', border: 0, opacity: this.isResetButtonDisabled() && !isSelfApprovalBlocked ? "1" : "0.5" }}
                                    onClick={() => this.handleRun('reset')}
                                    disabled={!this.isResetButtonDisabled() || isSelfApprovalBlocked}
                                >
                                    <img src={ResetApprovalIcon} alt="ResetApprovalIcon" /> {t('Reset')}
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>
                }

                <UserPromptModal
                    source={userPromptModal.source}
                    header={userPromptModal.header}
                    showModal={userPromptModal.showModal}
                    sourceInfo={userPromptModal.sourceInfo}
                    actions={userPromptModal.actions}
                    actionMethods={userPromptModal.actionMethods}
                />
            </div>
        );
    }
}

export default inject('timelogStore', 'absenceStore', 'commonStore', 'userStore', 'scheduleStore', 'vacationStore'
)(applicationRouter(withLocalization(observer(MultiTaskModalAccept))));